import { z } from 'zod';
import { hasPasswordSpecialCharacter } from '../../../../utils/Regex';

export type PasswordRuleItem = {
  isValid: boolean;
  label: string;
};

export const PASSWORD_RULE_LABELS = {
  case: 'Must Contain Atleast 1 Uppercase & lowercase characters',
  number: 'Must Contain Atleast 1 Number',
  special: 'Must Contain Atleast 1 special character',
  whitespace: 'Whitespace not allowed',
  minLength: 'Must be Atleast 8 characters',
} as const;

export const passwordRuleChecks = {
  case: (password: string) => /[a-z]/.test(password) && /[A-Z]/.test(password),
  number: (password: string) => /\d/.test(password),
  special: (password: string) => hasPasswordSpecialCharacter(password),
  whitespace: (password: string) => !/\s/g.test(password) && password.length > 1,
  minLength: (password: string) => password.length >= 8,
} as const;

export const getPasswordRuleItems = (password: string): PasswordRuleItem[] => [
  {
    isValid: passwordRuleChecks.case(password),
    label: PASSWORD_RULE_LABELS.case,
  },
  {
    isValid: passwordRuleChecks.number(password),
    label: PASSWORD_RULE_LABELS.number,
  },
  {
    isValid: passwordRuleChecks.special(password),
    label: PASSWORD_RULE_LABELS.special,
  },
  {
    isValid: passwordRuleChecks.whitespace(password),
    label: PASSWORD_RULE_LABELS.whitespace,
  },
  {
    isValid: passwordRuleChecks.minLength(password),
    label: PASSWORD_RULE_LABELS.minLength,
  },
];

const newPasswordSchema = z
  .string()
  .min(1, 'Password is required')
  .refine(passwordRuleChecks.case, { message: '' })
  .refine(passwordRuleChecks.number, { message: '' })
  .refine(passwordRuleChecks.special, { message: '' })
  .refine(passwordRuleChecks.whitespace, { message: '' })
  .refine(passwordRuleChecks.minLength, { message: '' });

export const createGoogleUserPasswordSchema = z
  .object({
    newPassword: newPasswordSchema,
    confirmPassword: z.string().min(1, 'Confirm Password is required'),
  })
  .refine((data) => data.confirmPassword === data.newPassword, {
    message: 'Password not matching',
    path: ['confirmPassword'],
  });

export type CreateGoogleUserPasswordFormValues = z.infer<typeof createGoogleUserPasswordSchema>;
